// src/api.js
import axios from "axios";
import { ADMIN_BASE_PATH, ADMIN_LOGIN_PATH } from "./routes";

/* ================== API BASE ================== */
// .env da REACT_APP_API_BASE_URL bo'lsa – undan, bo'lmasa joriy domen.
const RAW = (
  process.env.REACT_APP_API_BASE_URL || window.location.origin
).replace(/\/+$/, "");

export const API_BASE = RAW.toLowerCase().endsWith("/api") ? RAW : `${RAW}/api`;

/* ================== Token storage ================== */
const TOKENS = {
  get access() {
    return localStorage.getItem("access");
  },
  set access(v) {
    if (v) localStorage.setItem("access", v);
    else localStorage.removeItem("access");
  },
  get refresh() {
    return localStorage.getItem("refresh");
  },
  set refresh(v) {
    if (v) localStorage.setItem("refresh", v);
    else localStorage.removeItem("refresh");
  },
  clear() {
    this.access = null;
    this.refresh = null;
  },
};

// Eski kod bilan mos kelishi uchun: setTokens({ access, refresh })
export function setTokens(tokens) {
  const { access, refresh } = tokens || {};
  TOKENS.access = access || null;
  if (typeof refresh !== "undefined") {
    TOKENS.refresh = refresh || null;
  }
}

/* ================== Navigatsiya helper ================== */
export function logoutToLogin(reason = "session_expired") {
  TOKENS.clear();
  try {
    localStorage.removeItem("yt_user");
  } catch (_) {}

  const here = (window.location.pathname || "").toLowerCase();
  const target =
    ADMIN_LOGIN_PATH + (reason ? `?reason=${encodeURIComponent(reason)}` : "");
  if (!here.startsWith(ADMIN_LOGIN_PATH.toLowerCase())) {
    window.location.assign(target);
  }
}

/* ================== Axios instance ================== */
export const api = axios.create({
  // masalan: http://localhost:8000/api/
  baseURL: `${API_BASE}/`,
});

/* ================== Auth URL helper ================== */
// Auth endpointlarida Authorization qo'shmaymiz va refresh loopga tushmaymiz
const isAuthPath = (fullUrl = "") => {
  const lower = (fullUrl || "").toLowerCase();
  // absolute bo‘lsa host qismini olib tashlaymiz va // ni / ga qisamiz
  const path = lower
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/\/{2,}/g, "/");

  return (
    path.startsWith("/api/token/") ||
    path.startsWith("/api/token/refresh/") ||
    path.startsWith("/api/auth/login/") ||
    path.startsWith("/api/auth/logout/")
  );
};

/* ========== Request: Authorization header qo'shish ========== */
api.interceptors.request.use((config) => {
  const absUrl = (config.baseURL || "") + (config.url || "");
  if (!isAuthPath(absUrl)) {
    const t = TOKENS.access;
    if (t) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
      }
    }
  }
  return config;
});

/* ========== Response: 401 -> refresh (single-flight) ========== */
let refreshPromise = null;
const subscribers = [];

function addSubscriber(cb) {
  subscribers.push(cb);
}
function notifySubscribers(token) {
  while (subscribers.length) {
    try {
      subscribers.shift()(token);
    } catch (_) {}
  }
}

async function doRefresh() {
  if (!TOKENS.refresh) throw new Error("NO_REFRESH_TOKEN");
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/token/refresh/`, { refresh: TOKENS.refresh })
      .then((r) => {
        const newAccess = r?.data?.access;
        if (!newAccess) throw new Error("NO_ACCESS_IN_REFRESH_RESPONSE");
        TOKENS.access = newAccess;
        return newAccess;
      })
      .finally(() => {
        setTimeout(() => (refreshPromise = null), 0);
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error || {};
    if (!response || !config) return Promise.reject(error);

    const status = response.status;
    const absUrl = (config.baseURL || "") + (config.url || "");
    const authPath = isAuthPath(absUrl);

    // Faqat auth bo'lmagan endpointlarda 401 ni auto-refresh qilamiz
    if (status === 401 && !config._retry && !authPath) {
      config._retry = true;
      try {
        const newAccess = await new Promise((resolve, reject) => {
          addSubscriber((t) =>
            t ? resolve(t) : reject(new Error("REFRESH_FAILED"))
          );
          doRefresh().then(
            (t) => notifySubscribers(t),
            () => notifySubscribers(null)
          );
        });

        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newAccess}`;
        return api(config);
      } catch {
        TOKENS.clear();
        try { localStorage.removeItem("yt_user"); } catch (_) {}

        const here = (window.location.pathname || "").toLowerCase();
        const onAdmin = here.startsWith(ADMIN_BASE_PATH.toLowerCase());

        if (onAdmin && !here.startsWith(ADMIN_LOGIN_PATH.toLowerCase())) {
          window.location.assign(`${ADMIN_LOGIN_PATH}?reason=session_expired`);
          return Promise.reject(error);
        }

        // Shop sahifasi: token o'chirildi, auth header'siz qayta so'rov yuboramiz
        const retryHeaders = { ...(config.headers || {}) };
        delete retryHeaders.Authorization;
        delete retryHeaders.authorization;
        return api({ ...config, headers: retryHeaders });
      }
    }

    return Promise.reject(error);
  }
);

/* ================== Auth helpers ================== */

/** Access tokenni qo'lda o'rnatish (kerak bo'lsa) */
export const setAuthToken = (token) => {
  TOKENS.access = token || null;
};

/**
 * PIN-login: backend /api/auth/login/
 * Body: { username, code }
 * Response: { access, refresh, user }
 */
export async function authLogin(username, code) {
  const res = await api.post("auth/login/", {
    username,
    code, // PIN (harf+raqam bo'lishi ham mumkin)
  });

  const { access, refresh, user } = res.data || {};

  if (!access) {
    // backend noto‘g‘ri javob qaytarsa
    throw new Error("ACCESS_TOKEN_NOT_RETURNED");
  }

  // Tokenlarni saqlaymiz
  setTokens({ access, refresh });

  // Foydalanuvchi ma'lumotini saqlaymiz
  try {
    localStorage.setItem("yt_user", JSON.stringify(user || null));
  } catch (e) {
    console.warn("yt_user localStorage ga yozilmadi:", e);
  }

  // Login.js ga { access, refresh, user } qaytaramiz
  return { access, refresh, user };
}

/** Hozirgi foydalanuvchi ma'lumoti (role, amali, perms va hokazo) */
export async function fetchMe() {
  const { data } = await api.get("me/");
  return data;
}

/** Logout — agar backendda /auth/logout/ mavjud bo'lsa, urib ko'ramiz */
export async function authLogout() {
  try {
    await api.post("auth/logout/");
  } catch (_) {
    // mavjud bo'lmasa jim
  } finally {
    TOKENS.clear();
    try {
      localStorage.removeItem("yt_user");
    } catch (_) {}
  }
}

export default api;
